import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { Text, Row, Col, FlexView, Separator } from '@cuemath/comps';
import COLORS from 'constants/colors';
import Tooltip from 'comps/tooltip';
import { RATING_EMOJI } from 'constants/reviews';
import { PlusIcon } from 'comps/icon';
import Transcribe from './transcribe';

const StudentDetailsCol = styled(Col)`
  flex: 1;
`;

const TextElipse = styled(Text)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 8em;
  font-weight: 500;
`;

const Wrapper = styled(FlexView)`
  position: relative;
  min-height: 58px;
  width: 95%;
  padding-bottom: 6px;
`;

const PlusWrapper = styled(Col)`
  position: absolute;
  left: 300px;
  cursor: pointer;
`;

const ColWrapper = styled(Col)`
  position: relative;
  left: 20px;
`;

const LinkStyle = styled.a`
  cursor: pointer;
  color: ${COLORS.BOSTON_BLUE};
  :visited {
    color: ${COLORS.PURPLE};
  }
  :hover {
    color: blue;
  }
`;

const padNum = num => {
  return ('0' + num).slice(-2);
};

const getMinutes = totalSeconds => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds - hours * 3600 - minutes * 60;

  return `${padNum(hours)}:${padNum(minutes)}:${padNum(seconds)}`;
};

const LogDetails = ({
  classCallCount,
  classCallDuration,
  studentCallLogs,
  classCallLogs,
  isFormDisabled,
  onLogClicked,
  onPlusClicked,
  plusValues,
  totalLogsCount,
}) => {
  const showLogsData = logsData => {
    const { logsArr, fName } = logsData || {};

    return (
      <>
        {logsArr &&
          logsArr.length &&
          logsArr.map(stCall => {
            const {
              call_duration: callDur,
              log_id: logId,
              log_number: logNumber,
              log_url: logUrl,
            } = stCall || {};

            const recordObj = {
              duration: callDur ? getMinutes(callDur) : '00:00',
              isFormDisabled,
              logData: stCall,
              logId,
              logNumber,
              logUrl,
              plusValues,
              recordName: `${fName}-${logNumber}`,
            };

            return getLogs(recordObj);
          })}
      </>
    );
  };

  const getLogs = dataObj => {
    const {
      duration,
      isFormDisabled,
      logData,
      logId,
      logNumber,
      logUrl,
      plusValues,
      recordName,
    } = dataObj || {};
    const { is_valid: isValid, log_type: logType, call_type: callType } = logData || {};
    const isValidText = isValid ? 'Valid' : 'Not Valid';

    return (
      <Row alignItems="center" key={`${logId}-${logNumber}`}>
        <Col>
          <LinkStyle
            onClick={() => onLogClicked(logUrl, callType, logType, totalLogsCount)}
            href={logUrl}
            rel="noopener noreferrer"
            target="_blank"
          >
            Call-{logNumber > 9 ? logNumber : `0${logNumber}`}
          </LinkStyle>
        </Col>
        <Col>
          <Text>{duration}</Text>
        </Col>
        {callType && (
          <Col>
            <Text>{String(callType).toUpperCase()}</Text>
          </Col>
        )}

        {logType === 'AV_CALL' && (
          <Col>
            <Text>{isValidText}</Text>
          </Col>
        )}
        {logType === 'GROUP_CALL' && <Transcribe logUrl={logUrl} />}

        <PlusWrapper>
          {!plusValues[logId] && !isFormDisabled && (
            <PlusIcon
              size={14}
              key={logId}
              name={recordName}
              onClick={() => onPlusClicked(logId, logData)}
            />
          )}
          {plusValues[logId] && (
            <span
              role="img"
              key={logId}
              aria-label="check_mark"
              onClick={() => (!isFormDisabled ? onPlusClicked(logId, null) : '')}
            >
              ✅
            </span>
          )}
        </PlusWrapper>
      </Row>
    );
  };

  if (studentCallLogs && Array.isArray(studentCallLogs) && studentCallLogs.length) {
    return (
      <>
        {' '}
        {studentCallLogs.map(cl => {
          const {
            student_call_logs: studentCallLogs,
            student_call_count: callCount,
            student_call_duration: callDuration,
            student_grade: studentGrade,
            student_name: studentName,
            student_id: studentId,
            screen_logs: screenLogs,
            screen_logs_count: screenCallCount,
            rating = 'NA',
          } = cl || {};

          if (callCount + screenCallCount === 0) {
            return (
              <Wrapper key={studentId}>
                <StudentDetailsCol>
                  <Text> Call logs not available</Text>
                </StudentDetailsCol>
              </Wrapper>
            );
          }
          const name = studentName ? studentName.split(' ') : [];
          const [fName] = name || [];

          const avLogsObj = {
            logsArr: studentCallLogs,
            fName,
          };

          const screenLogObj = {
            logsArr: screenLogs,
            fName,
          };

          return (
            <Wrapper key={studentId}>
              <Row gutter={8} gap={8} nowrap key={studentId}>
                <StudentDetailsCol>
                  <Row justifyContent="space-between" key={studentName}>
                    <Row gutter={0}>
                      <Tooltip title={studentName} position="bottom" width="100px">
                        <TextElipse>{studentName}</TextElipse>
                      </Tooltip>
                      <Text>{studentGrade && `(Grade ${studentGrade})`}</Text>
                    </Row>
                    {callCount > 0 && (
                      <ColWrapper>
                        <Tooltip title="Number of calls" position="bottom">
                          {callCount}
                        </Tooltip>{' '}
                        |{' '}
                        <Tooltip title="Duration of calls" position="bottom">
                          {getMinutes(callDuration)}
                        </Tooltip>{' '}
                        |{' '}
                        {rating && (
                          <Tooltip title="Rating" position="bottom">
                            {RATING_EMOJI[rating]}
                          </Tooltip>
                        )}
                      </ColWrapper>
                    )}
                  </Row>
                  <Separator height={1} />
                  {callCount > 0 && showLogsData(avLogsObj)}

                  {screenCallCount > 0 && (
                    <>
                      <Text>Screen Logs</Text>
                      <Separator height={1} />
                      {showLogsData(screenLogObj)}
                    </>
                  )}
                </StudentDetailsCol>
              </Row>
            </Wrapper>
          );
        })}
      </>
    );
  }

  const classCallLg = {
    logsArr: classCallLogs,
  };

  return (
    <Wrapper key="class_log">
      <StudentDetailsCol>
        <Row gutter={8} gap={8} justifyContent="flex-end">
          {classCallCount > 0 && (
            <ColWrapper>
              <Tooltip title="Number of calls" position="bottom">
                {classCallCount}
              </Tooltip>{' '}
              |{' '}
              <Tooltip title="Duration of calls" position="bottom">
                {getMinutes(classCallDuration)}
              </Tooltip>{' '}
              | <Separator height={1} />
            </ColWrapper>
          )}
        </Row>
        {showLogsData(classCallLg)}
      </StudentDetailsCol>
    </Wrapper>
  );
};

LogDetails.propTypes = {
  classCallCount: PropTypes.number,
  classCallDuration: PropTypes.number,
  callDuration: PropTypes.string,
  classCallLogs: PropTypes.array,
  handleOptionChange: PropTypes.func,
  isFormDisabled: PropTypes.bool.isRequired,
  logsReviewed: PropTypes.array,
  onPlusClicked: PropTypes.func.isRequired,
  onLogClicked: PropTypes.isRequired,
  plusValues: PropTypes.object.isRequired,
};

LogDetails.defaultProps = {
  classCallCount: 0,
  classCallDuration: 0,
  classLogs: null,
  callDuration: null,
  handleOptionChange: null,
  logsReviewed: null,
  totalLogsCount: 0,
};

export default LogDetails;
